import * as Sentry from "@sentry/react";
import {
  FinixPaymentForm,
  IFinixPaymentFormHandle,
  IFinixTokenizationError,
} from "components/FinixPaymentForm/FinixPaymentForm";
import { IFinixTokenizationErrorDetails } from "components/FinixPaymentForm/finixTokenizationError";
import { installmentScheduleTypeConstants } from "constants/account";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useFinixFraudSession } from "hooks/useFinixJs";
import { usePatientOrderToken } from "hooks/usePatientOrderToken";
import { debounce } from "lodash";
import { BackToDetailsButton } from "pages/OrderOverview/components/BackToDetailsButton/BackToDetailsButton";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetCustomsByOrderTypeAndAccountIdQuery } from "store/queries/customize";
import {
  IFinixCardFeeQuoteResponse,
  useCancelFinixSplitAuthMutation,
  useCaptureFinixSplitPaymentMutation,
  useCreateFinixPaymentMutation,
  useCreateFinixSplitAuthMutation,
  useGetFinixSavedPaymentMethodQuery,
  usePayFinixRemainingWithSavedCardMutation,
  useQuoteFinixCardFeeMutation,
} from "store/queries/finix";
import { useGetCurrentLegalDocumentsQuery } from "store/queries/legalDocuments";
import { TourAnchor } from "training/anchors";
import { ICreatePaymentTermsAcceptanceDto } from "types/LegalDocuments/LegalDocuments";
import { IOrder } from "types/Order/Order";
import { TermsAcceptanceContext } from "types/TermsAcceptance/TermsAcceptance";

import {
  addMonthsLikeBackend,
  addWeeksLikeBackend,
  adjustInstallmentDateForWeekend,
  getEstimatedCost,
  getInitialPaymentInCents,
} from "../Overview/helpers";
import { CardAccordion } from "../PaymentSection/components/CardAccordion";
import {
  HEADER_PANEL_TEXT,
  HeaderPanel,
} from "../PaymentSection/components/HeaderPanel";
import { LegalDocumentDialog } from "../PaymentSection/components/LegalDocumentDialog";
import { PaymentSplitSection } from "../PaymentSection/components/PaymentSplitSection";
import { SavedCard } from "../PaymentSection/components/SavedCard";
import { PaymentSuccessFull } from "../PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import {
  AutopayWarningBanner,
  ContainerForm,
  StyledButton,
  StyledInputNumber,
  StyledSplitButton,
} from "../PaymentSection/styled";
import {
  CreditCardSurchargeAlert,
  creditCardFeeForAmount,
  ICardFeePreview,
  PaymentFeeSummary,
  PaymentFeeSummaryState,
  PaymentMethodFeesNotice,
  paymentMethodFeesText,
  quoteMatchesPreview,
  splitFeesMatchPreview,
} from "./PaymentFees";
import { reportFinixTokenizationFailure } from "./reportFinixTokenizationFailure";
import { useCardFeePreview } from "./useCardFeePreview";

const SECURE_TRANSACTIONS_TEXT = "All transactions are secure and encrypted";

interface ISplitAuthResult {
  success: boolean;
  authorizationIds: [string, string];
  paymentInstrumentIds: [string, string];
  buyerIdentityId: string;
  // CLE-2798
  creditCardFeesInCents?: [number, number];
  cardTypes?: [string | null, string | null];
  creditCardFeePercent?: number;
}

const PAYMENT_PLAN_AUTHORIZATION_TEXT =
  "I authorize Clear Health to automatically charge the provided credit card for the specified (and updated if applicable) amount in the Payment Plan for each billing period";

interface IFinixPaymentSectionProps {
  order: IOrder;
  prices: {
    clearPrice: number;
    totalSaving: number;
    fullPrice: number;
  };
  finixApplicationId: string;
  finixMerchantId?: string;
  finixEnvironment: "sandbox" | "live";
  showPaymentSection: {
    paymentPlan: boolean;
    paymentSection: boolean;
    paymentSuccess: boolean;
    receiptAvailable: boolean;
  };
  setShowPaymentSection: any;
  onReturnClick?: () => void;
  onUpdateOrder?: () => void;
  isPaymentPlan?: boolean;
  isDownPaymentOnly?: boolean;
  /** Days from date of service after which the negotiated discount expires. */
  expirationDays?: number;
  /** When true, the user is paying down an existing PaymentPlan's remaining balance.
   *  prices.clearPrice is the current remaining amount. User picks a custom amount
   *  between $1 and clearPrice and either uses a saved Finix PI or enters a new card. */
  payRemainingPrice?: boolean;
}

export function FinixPaymentSection({
  order,
  prices,
  finixApplicationId,
  finixMerchantId,
  finixEnvironment,
  showPaymentSection,
  setShowPaymentSection,
  onReturnClick,
  onUpdateOrder,
  isPaymentPlan = false,
  isDownPaymentOnly = false,
  expirationDays = 0,
  payRemainingPrice = false,
}: IFinixPaymentSectionProps) {
  useDocumentTitle("Payment Page");

  const { pageViewEvents } = useAnalytics();
  // Read the ref object (not its `.current`) so showError reads the freshest
  // toast instance at call time rather than capturing a possibly-null value
  // from an earlier render.
  const toastRef = useContext(ToastContext);

  // Initialize Finix.Auth for on-session fraud detection — its getSessionKey() is
  // called right before each auth/transfer mutation so the key is fresh.
  const { getFraudSessionId } = useFinixFraudSession(
    finixEnvironment,
    finixMerchantId
  );

  const firstFormRef = useRef<IFinixPaymentFormHandle>(null);
  const secondFormRef = useRef<IFinixPaymentFormHandle>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Inline payment-error banner (persists until next attempt). Backstops the
  // Toast notification, which has a 6s life and can be missed if the user is
  // focused on the form.
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [firstCardComplete, setFirstCardComplete] = useState(false);

  const { data: legalDocuments, isLoading: isLegalDocumentsFetching } =
    useGetCurrentLegalDocumentsQuery();

  const { data: contentData } = useGetCustomsByOrderTypeAndAccountIdQuery(
    { orderType: order.orderType, accountId: order.account.id },
    { skip: !order }
  );
  const { overviewPage: overviewContent } = contentData?.data || {};

  // "01/01/2999" is the placeholder date of service used when a visit isn't
  // scheduled yet — the discount-expiry banner is meaningless in that case.
  const notScheduledYet = parseDate(order.dateOfService) === "01/01/2999";

  const [openLegalDocument, setOpenLegalDocument] = useState<
    "terms" | "privacy" | null
  >(null);
  // Built in handleSubmit, consumed by the tokenize-then-charge single-card
  // flow whose async callbacks can't easily receive parameters.
  const termsAcceptanceRef = useRef<
    ICreatePaymentTermsAcceptanceDto | undefined
  >(undefined);
  const [secondCardComplete, setSecondCardComplete] = useState(false);

  // Split payment state
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [firstCardAmount, setFirstCardAmount] = useState<number>(0);
  const [secondCardAmount, setSecondCardAmount] = useState<number>(0);
  const [preferredPaymentMethodIndex, setPreferredPaymentMethodIndex] =
    useState<0 | 1>(0);

  // Pay-remaining-price state (parity with Stripe PaymentSection)
  const [editableRemainingAmount, setEditableRemainingAmount] =
    useState<number>(payRemainingPrice ? prices.clearPrice : 0);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState<any>(null);
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = useState(false);
  const [autopayWarningDismissed, setAutopayWarningDismissed] = useState(false);

  // CLE-2181: this endpoint takes an order-bound patient token; wait until the token in
  // redux belongs to THIS order before firing.
  const { isReady: isPatientTokenReady } = usePatientOrderToken(order);

  const {
    data: savedPaymentMethodData,
    isLoading: isSavedPaymentMethodLoading,
  } = useGetFinixSavedPaymentMethodQuery(order.id, {
    skip: !payRemainingPrice || !isPatientTokenReady,
  });

  useEffect(() => {
    if (savedPaymentMethodData?.data && payRemainingPrice) {
      setSavedPaymentMethod(savedPaymentMethodData.data);
      setUseSavedPaymentMethod(true);
    }
  }, [savedPaymentMethodData, payRemainingPrice]);

  const [payFinixRemainingWithSavedCard] =
    usePayFinixRemainingWithSavedCardMutation();

  // Promise resolvers for tokenization (allow wrapping the callback-based form API)
  const firstCardTokenResolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  } | null>(null);
  const secondCardTokenResolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  } | null>(null);

  const [createFinixPayment] = useCreateFinixPaymentMutation();
  const [createFinixSplitAuth] = useCreateFinixSplitAuthMutation();
  const [captureFinixSplitPayment] = useCaptureFinixSplitPaymentMutation();
  const [cancelFinixSplitAuth] = useCancelFinixSplitAuthMutation();
  const [quoteFinixCardFee] = useQuoteFinixCardFeeMutation();

  // CLE-2798 — credit-card fee. The order carries a per-payer % that applies only when Finix reports
  // the card as CREDIT, which we only learn after tokenization. So: tokenize → quote → if a fee
  // applies, stop and show it (the patient can back out) → charge with the acknowledged fee.
  const orderCreditCardFeePercent = Number(
    order.gfeCreditCardFeeValueInPercentage ?? 0
  );
  // Single / saved card: the quote waiting for the patient's confirmation.
  const [cardFeeQuote, setCardFeeQuote] =
    useState<IFinixCardFeeQuoteResponse | null>(null);
  // The card was read and carries no surcharge: "No payment fee" is shown while it is charged.
  const [noFeeCardConfirmed, setNoFeeCardConfirmed] = useState(false);
  // CLE-2798: the card read in the background once its fields are complete, so the surcharge rows and
  // the "Pay Now · total" label are on screen before the patient presses Pay (one click, as in the new
  // checkout design). The ref lets the Pay handlers read the value that was on screen.
  // Each card of a two-card payment is read the same way. The refs let the Pay handlers, defined
  // before the reads, see what was on screen.
  const firstCardPreviewRef = useRef<ICardFeePreview | null>(null);
  const secondCardPreviewRef = useRef<ICardFeePreview | null>(null);
  const feeSummaryStateRef = useRef<PaymentFeeSummaryState | null>(null);
  // Two cards: the holds are placed (they include the fee); waiting for confirmation before capture.
  const [pendingSplitCapture, setPendingSplitCapture] = useState<{
    authResponse: ISplitAuthResult;
    firstAmountCents: number;
    secondAmountCents: number;
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto;
  } | null>(null);

  const publicContact = useCallback(() => {
    const orderData = order as any;
    const provider = orderData?.providers?.[0];
    return {
      isVisible: !!(
        provider?.publicContactName &&
        provider?.publicContactRole &&
        provider?.publicContactPhone
      ),
      name: provider?.publicContactName || "",
      role: provider?.publicContactRole || "",
      phone: provider?.publicContactPhone || "",
    };
  }, [order]);

  const activePaymentPlan = useMemo(
    () => order?.paymentPlan?.find((plan) => plan?.active),
    [order]
  );

  // For pay-remaining-price: surface the existing payment plan's status (X payments
  // left, per-period installment, next due date) and the "does not replace your upcoming
  // automatic payment" warning. Only meaningful when the active plan has installments.
  const remainingInstallmentsInfo = useMemo(() => {
    const installments = activePaymentPlan?.installments;
    if (!installments?.length) return null;
    const remaining = installments.filter((inst) => !inst.paymentId);
    if (!remaining.length) return null;
    const frequencySuffix: Record<string, string> = {
      Monthly: "/mo",
      Weekly: "/weekly",
      Biweekly: "/BiWeekly",
    };
    return {
      remainingPayments: remaining.length,
      installmentAmountInCents: remaining[0]?.amountInCents ?? 0,
      nextDateDue: remaining[0]?.dateDue,
      frequencySuffix:
        frequencySuffix[activePaymentPlan?.installmentFrequency ?? "Monthly"] ??
        "/mo",
    };
  }, [activePaymentPlan]);

  const estimatedCost = useMemo(
    () =>
      getEstimatedCost(order.orderType, prices.clearPrice, prices.fullPrice),
    [prices, order]
  );

  // CLE-2791 — this deposit was previously computed by the parent off `prices.clearPrice`, the
  // DISCOUNTED total. The discount is a pay-in-full incentive that a payment plan does not get, so
  // Finix asked GFE patients for a smaller deposit than Stripe did on the same plan.
  const initialPaymentInCents = useMemo(
    () =>
      getInitialPaymentInCents({
        orderType: order.orderType,
        clearPrice: prices.clearPrice,
        fullPrice: prices.fullPrice,
        paymentPlanMinDownPaymentPercent:
          activePaymentPlan?.paymentPlanMinDownPaymentPercent,
        paymentPlanMinDownPaymentFlat:
          activePaymentPlan?.paymentPlanMinDownPaymentFlat,
      }),
    [activePaymentPlan, order.orderType, prices]
  );

  const paymentPlanDuration = activePaymentPlan?.paymentPlanDuration;

  const installmentFrequency = (activePaymentPlan?.installmentFrequency ??
    "Monthly") as "Monthly" | "Weekly" | "Biweekly";

  const installmentFrequencySuffix = useMemo(() => {
    const suffixByFrequency: Record<string, string> = {
      Monthly: "/mo",
      Weekly: "/weekly",
      Biweekly: "/BiWeekly",
    };
    return suffixByFrequency[installmentFrequency] ?? "/mo";
  }, [installmentFrequency]);

  const installmentPaymentForPaymentPlan = useMemo(() => {
    // Set-amount plans carry a fixed per-installment amount — show it as-is
    // instead of dividing the remaining balance across the duration.
    if (
      activePaymentPlan?.calculationBasis === "InstallmentAmount" &&
      activePaymentPlan.installmentAmountInCents != null
    ) {
      return centsToDollars(activePaymentPlan.installmentAmountInCents);
    }
    if (!paymentPlanDuration) return 0;
    const remainingAmount =
      estimatedCost - centsToDollars(initialPaymentInCents);
    return Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
  }, [
    activePaymentPlan,
    estimatedCost,
    initialPaymentInCents,
    paymentPlanDuration,
  ]);

  const nextInstallmentDueDate = useMemo(() => {
    const { installmentScheduleType } = order.account;
    const isTwoWeekSchedule =
      installmentScheduleType ===
      installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;
    let startDate: dayjs.Dayjs;
    let addAPeriod = true;
    if (isTwoWeekSchedule) {
      const twoWeeksBefore = dayjs
        .utc(order.dateOfService)
        .subtract(14, "days");
      const now = dayjs.utc();

      startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
      addAPeriod = !!twoWeeksBefore.isBefore(now);
    } else {
      startDate = dayjs();
    }

    // Mirror generateInstallments (used by the Home timeline): use backend-style
    // date math and shift weekend due dates so the dates shown here match.
    const startNativeDate = startDate.toDate();
    const installments = Array.from(
      { length: paymentPlanDuration || 0 },
      (_, index) => {
        const periodsFromStart = addAPeriod ? index + 1 : index;
        let dueNative: Date;
        if (installmentFrequency === "Weekly") {
          dueNative = adjustInstallmentDateForWeekend(
            addWeeksLikeBackend(startNativeDate, periodsFromStart)
          );
        } else if (installmentFrequency === "Biweekly") {
          dueNative = adjustInstallmentDateForWeekend(
            addWeeksLikeBackend(startNativeDate, periodsFromStart * 2)
          );
        } else {
          dueNative = adjustInstallmentDateForWeekend(
            addMonthsLikeBackend(startNativeDate, periodsFromStart)
          );
        }
        return dayjs(dueNative).format("MM/DD/YYYY");
      }
    );
    return installments[0];
  }, [order, paymentPlanDuration, installmentFrequency]);

  const totalAmountInCents = useMemo(() => {
    if (payRemainingPrice) {
      return Math.round(editableRemainingAmount * 100);
    }
    if (isPaymentPlan && initialPaymentInCents) {
      return initialPaymentInCents;
    }
    return Math.round(prices.clearPrice * 100);
  }, [
    payRemainingPrice,
    editableRemainingAmount,
    isPaymentPlan,
    initialPaymentInCents,
    prices.clearPrice,
  ]);

  const totalPaymentAmount = useMemo(
    () => centsToDollars(totalAmountInCents),
    [totalAmountInCents]
  );

  const displayAmount = useMemo(() => {
    if (payRemainingPrice) {
      return prices.clearPrice;
    }
    if (isPaymentPlan && initialPaymentInCents) {
      return centsToDollars(initialPaymentInCents);
    }
    return prices.clearPrice;
  }, [
    payRemainingPrice,
    isPaymentPlan,
    initialPaymentInCents,
    prices.clearPrice,
  ]);

  const headerTitle = useMemo(() => {
    if (payRemainingPrice) {
      return "Remaining amount";
    }
    if (isPaymentPlan) {
      return isDownPaymentOnly ? "Down Payment" : "Initial Payment";
    }
    return "Checkout";
  }, [payRemainingPrice, isPaymentPlan, isDownPaymentOnly]);

  const handleAmountChange = (e: any): void => {
    const value =
      typeof e?.value === "number" ? e.value : parseFloat(e?.target?.value);
    if (!Number.isNaN(value) && value >= 1 && value <= prices.clearPrice) {
      setEditableRemainingAmount(value);
    }
    if (Number.isNaN(value)) {
      setEditableRemainingAmount(1);
    }
  };

  // Keep firstCardAmount in sync with the split
  useEffect(() => {
    if (isSplitPayment) {
      setFirstCardAmount(totalPaymentAmount - secondCardAmount);
    } else {
      setFirstCardAmount(totalPaymentAmount);
    }
  }, [secondCardAmount, totalPaymentAmount, isSplitPayment]);

  const handleAddSecondCard = () => {
    setIsSplitPayment(true);
    const half = Math.floor(totalPaymentAmount / 2);
    setSecondCardAmount(half);
    setFirstCardAmount(totalPaymentAmount - half);
    setSecondCardComplete(false);
    setPreferredPaymentMethodIndex(0);
  };

  const handleRemoveSecondCard = (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsSplitPayment(false);
    setSecondCardAmount(0);
    setFirstCardAmount(totalPaymentAmount);
    setSecondCardComplete(false);
    setPreferredPaymentMethodIndex(0);
  };

  const handlePaymentSplitFirstCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(totalPaymentAmount - clamped);
  };

  const handlePaymentSplitSecondCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(clamped);
  };

  const payNowDisabled = useMemo(() => {
    if (!termsAccepted) {
      return true;
    }
    if (payRemainingPrice) {
      // Saved PI path: amount must be valid, nothing else required
      if (useSavedPaymentMethod && savedPaymentMethod) {
        return (
          editableRemainingAmount < 1 ||
          editableRemainingAmount > prices.clearPrice ||
          isProcessing
        );
      }
      // New card path: amount valid + card fields complete
      return (
        !firstCardComplete ||
        editableRemainingAmount < 1 ||
        editableRemainingAmount > prices.clearPrice ||
        isProcessing
      );
    }
    if (isSplitPayment) {
      return (
        !firstCardComplete ||
        !secondCardComplete ||
        secondCardAmount <= 0 ||
        secondCardAmount >= totalPaymentAmount ||
        isProcessing
      );
    }
    return !firstCardComplete || isProcessing;
  }, [
    payRemainingPrice,
    useSavedPaymentMethod,
    savedPaymentMethod,
    editableRemainingAmount,
    prices.clearPrice,
    isSplitPayment,
    firstCardComplete,
    secondCardComplete,
    secondCardAmount,
    totalPaymentAmount,
    isProcessing,
    termsAccepted,
  ]);

  const debouncedPageViewEvents = useMemo(
    () =>
      debounce((orderData: any, event: string) => {
        pageViewEvents(
          {
            accountName: orderData?.accountName,
            orderType: orderData?.orderType,
            communicationMethod: orderData?.communicationMethod,
          },
          event
        );
      }, 500),
    [pageViewEvents]
  );

  // Cancel any pending debounced analytics call on unmount to avoid a leak
  useEffect(
    () => () => debouncedPageViewEvents.cancel(),
    [debouncedPageViewEvents]
  );

  const showError = useCallback(
    (summary: string, detail: string) => {
      toastRef?.current?.show({
        severity: "error",
        summary,
        detail,
        life: 6000,
      });
      setPaymentError(`${summary}: ${detail}`);
    },
    [toastRef]
  );

  const buildAcceptanceSnapshot = (
    context: TermsAcceptanceContext
  ): Record<string, unknown> | undefined => {
    const displayedText: Record<string, string> = {
      secureTransactions: SECURE_TRANSACTIONS_TEXT,
      header: headerTitle,
      amount: parsePrice(displayAmount),
    };
    // CLE-2798: the surcharge disclosure shown before the card was entered.
    if (orderCreditCardFeePercent > 0) {
      displayedText.paymentMethodFees = paymentMethodFeesText(
        orderCreditCardFeePercent,
        !!isPaymentPlan
      );
    }
    // CLE-2798: the surcharge rows on screen when Pay was pressed (cards read in advance).
    const shown = feeSummaryStateRef.current;
    if (shown?.kind === "surcharge") {
      displayedText.creditCardSurcharge = `Credit card surcharge (${
        shown.percent
      }%) ${parsePrice(
        centsToDollars(shown.surchargeInCents)
      )} · Total payment ${parsePrice(
        centsToDollars(shown.amountInCents + shown.surchargeInCents)
      )}`;
    }

    if (context === TermsAcceptanceContext.DownPayment && !isDownPaymentOnly) {
      displayedText.paymentPlanAuthorization = PAYMENT_PLAN_AUTHORIZATION_TEXT;
      displayedText.paymentPlanAdjustment = HEADER_PANEL_TEXT;
      displayedText.paymentPlanInstallments = `${parsePrice(
        installmentPaymentForPaymentPlan
      )}${installmentFrequencySuffix}  ${paymentPlanDuration} payments`;
      displayedText.nextPaymentDue = `Next payment due ${nextInstallmentDueDate}`;
    }

    if (
      context === TermsAcceptanceContext.PayRemaining &&
      remainingInstallmentsInfo
    ) {
      displayedText.paymentPlanInstallments = `${
        remainingInstallmentsInfo.remainingPayments
      } payments left → ${parsePrice(
        centsToDollars(remainingInstallmentsInfo.installmentAmountInCents)
      )}${remainingInstallmentsInfo.frequencySuffix}`;
      if (remainingInstallmentsInfo.nextDateDue) {
        const nextDate = dayjs
          .utc(remainingInstallmentsInfo.nextDateDue)
          .format("M/D/YYYY");
        displayedText.nextPaymentScheduled = `Next payment scheduled for ${nextDate}`;
        if (editableRemainingAmount < prices.clearPrice) {
          displayedText.autopayNotice = `This payment does not replace your upcoming automatic payment on ${nextDate}.`;
        }
      }
    }

    return { displayedText };
  };

  const buildTermsAcceptance = async (
    context: TermsAcceptanceContext
  ): Promise<ICreatePaymentTermsAcceptanceDto | undefined> => {
    const termsAndConditionsVersionId =
      legalDocuments?.data?.termsAndConditions?.id;
    const privacyPolicyVersionId = legalDocuments?.data?.privacyPolicy?.id;
    if (!termsAndConditionsVersionId || !privacyPolicyVersionId) {
      return undefined;
    }
    let ipAddress: string | undefined;
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      ipAddress = data.ip;
    } catch {
      // IP lookup failed — proceed without it
    }
    return {
      acceptanceContext: context,
      termsAndConditionsVersionId,
      privacyPolicyVersionId,
      acceptanceSnapshot: buildAcceptanceSnapshot(context),
      ipAddress,
      userAgent: navigator.userAgent,
    };
  };

  // === Single-card (non-split) flow — unchanged from before ===

  // CLE-2798: the token is single-use, so the quote turns it into a Payment Instrument and the
  // charge reuses that instrument together with the fee the patient saw.
  const chargeSingleCard = async (source: {
    paymentInstrumentId: string;
    creditCardFeeAmountInCents: number;
  }) => {
    try {
      // Pay-remaining with a new card: backend routes to processFinixPayRemainingPrice
      // when payRemainingPrice=true. savePaymentMethod=true when the user selected the
      // new card as the preferred card for future autopays (index 1) on a partial payment.
      const isPartialRemaining =
        payRemainingPrice && editableRemainingAmount !== prices.clearPrice;
      const savePaymentMethod =
        payRemainingPrice &&
        preferredPaymentMethodIndex === 1 &&
        isPartialRemaining;

      const result = await createFinixPayment({
        orderId: order.id,
        paymentInstrumentId: source.paymentInstrumentId,
        creditCardFeeAmountInCents: source.creditCardFeeAmountInCents,
        amountInCents: totalAmountInCents,
        saveForFuture: isPaymentPlan,
        payRemainingPrice,
        savePaymentMethod,
        fraudSessionId: getFraudSessionId(),
        termsAcceptance: termsAcceptanceRef.current,
      }).unwrap();

      if (result.success) {
        pageViewEvents(
          {
            accountName: order.account?.name,
            orderType: order.orderType,
            communicationMethod: order.patient?.preferredContactMethod,
          },
          events.PURCHASE
        );

        if (onUpdateOrder) {
          onUpdateOrder();
        }

        setShowPaymentSection({
          paymentPlan: false,
          paymentSection: false,
          paymentSuccess: true,
          receiptAvailable: true,
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "Payment processing failed. Please try again.";

      Sentry.captureMessage("Finix payment processing failed", {
        level: "error",
        tags: {
          operation: "finix_payment_frontend",
          orderId: order.id,
        },
        extra: {
          orderId: order.id,
          amountInCents: totalAmountInCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSingleCardTokenSuccess = async (token: string) => {
    let quote: IFinixCardFeeQuoteResponse;
    try {
      quote = await quoteFinixCardFee({
        orderId: order.id,
        token,
        amountInCents: totalAmountInCents,
      }).unwrap();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "We could not read your card. Please try again.";
      Sentry.captureMessage("Finix card-fee quote failed", {
        level: "error",
        tags: { operation: "finix_card_fee_quote_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });
      showError("Card Error", errorMessage);
      setIsProcessing(false);
      return;
    }

    if (quote.feeApplies) {
      // The patient already saw exactly this total before pressing Pay: charge it.
      if (
        quoteMatchesPreview(
          firstCardPreviewRef.current,
          quote,
          totalAmountInCents
        )
      ) {
        await chargeSingleCard({
          paymentInstrumentId: quote.paymentInstrumentId,
          creditCardFeeAmountInCents: quote.feeAmountInCents,
        });
        return;
      }
      // Disclosure step: nothing is charged until the patient confirms the fee below.
      setCardFeeQuote(quote);
      setIsProcessing(false);
      return;
    }
    setNoFeeCardConfirmed(orderCreditCardFeePercent > 0);
    await chargeSingleCard({
      paymentInstrumentId: quote.paymentInstrumentId,
      creditCardFeeAmountInCents: 0,
    });
  };

  const handleSingleCardTokenError = (
    error: string,
    details?: IFinixTokenizationErrorDetails
  ) => {
    setIsProcessing(false);
    reportFinixTokenizationFailure({
      orderId: order.id,
      message: error,
      details,
      flow: "single_card",
    });
    showError("Card Error", error);
  };

  // Carries finix.js's parsed reason through the split flow's promise rejection.
  const toTokenizationError = (
    error: string,
    details?: IFinixTokenizationErrorDetails
  ): IFinixTokenizationError => {
    const err: IFinixTokenizationError = new Error(error);
    err.finixTokenizationError = details;
    return err;
  };

  // === Split-payment flow ===
  // The FinixPaymentForm uses callbacks (onTokenSuccess/onTokenError). To sequence
  // two tokenizations + backend calls, we wire each form's callbacks to a ref-held
  // Promise resolver, then await each tokenization in order.

  const handleFirstCardTokenSuccess = (token: string) => {
    if (isSplitPayment && firstCardTokenResolverRef.current) {
      firstCardTokenResolverRef.current.resolve(token);
      firstCardTokenResolverRef.current = null;
    } else if (isSplitPayment) {
      // Orphan tokenization — resolver was already consumed or never set. Reset UI so user can retry.
      Sentry.captureMessage("Finix split: orphan first-card token", {
        level: "warning",
        tags: { orderId: order.id },
      });
      setIsProcessing(false);
    } else {
      // Not in a split flow — fall through to single-card handling
      handleSingleCardTokenSuccess(token);
    }
  };

  const handleFirstCardTokenError = (
    error: string,
    details?: IFinixTokenizationErrorDetails
  ) => {
    if (isSplitPayment && firstCardTokenResolverRef.current) {
      firstCardTokenResolverRef.current.reject(
        toTokenizationError(error, details)
      );
      firstCardTokenResolverRef.current = null;
    } else if (isSplitPayment) {
      Sentry.captureMessage("Finix split: orphan first-card token error", {
        level: "warning",
        tags: { orderId: order.id },
        extra: { error },
      });
      setIsProcessing(false);
      showError("Card Error", error);
    } else {
      handleSingleCardTokenError(error, details);
    }
  };

  const handleSecondCardTokenSuccess = (token: string) => {
    if (secondCardTokenResolverRef.current) {
      secondCardTokenResolverRef.current.resolve(token);
      secondCardTokenResolverRef.current = null;
    } else {
      Sentry.captureMessage("Finix split: orphan second-card token", {
        level: "warning",
        tags: { orderId: order.id },
      });
      setIsProcessing(false);
    }
  };

  const handleSecondCardTokenError = (
    error: string,
    details?: IFinixTokenizationErrorDetails
  ) => {
    if (secondCardTokenResolverRef.current) {
      secondCardTokenResolverRef.current.reject(
        toTokenizationError(error, details)
      );
      secondCardTokenResolverRef.current = null;
    } else {
      Sentry.captureMessage("Finix split: orphan second-card token error", {
        level: "warning",
        tags: { orderId: order.id },
        extra: { error },
      });
      setIsProcessing(false);
      showError("Card Error", error);
    }
  };

  const tokenizeFirstCard = (): Promise<string> =>
    new Promise((resolve, reject) => {
      firstCardTokenResolverRef.current = { resolve, reject };
      if (firstFormRef.current) {
        firstFormRef.current.submit();
      } else {
        firstCardTokenResolverRef.current = null;
        reject(new Error("First card form is not ready"));
      }
    });

  const tokenizeSecondCard = (): Promise<string> =>
    new Promise((resolve, reject) => {
      secondCardTokenResolverRef.current = { resolve, reject };
      if (secondFormRef.current) {
        secondFormRef.current.submit();
      } else {
        secondCardTokenResolverRef.current = null;
        reject(new Error("Second card form is not ready"));
      }
    });

  const captureSplitPayment = async ({
    authResponse,
    firstAmountCents,
    secondAmountCents,
    termsAcceptance,
  }: {
    authResponse: ISplitAuthResult;
    firstAmountCents: number;
    secondAmountCents: number;
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto;
  }) => {
    const fees = authResponse.creditCardFeesInCents ?? [0, 0];
    // Capture both authorizations
    try {
      await captureFinixSplitPayment({
        orderId: order.id,
        authorizationIds: authResponse.authorizationIds,
        amounts: [firstAmountCents, secondAmountCents],
        paymentInstrumentIds: authResponse.paymentInstrumentIds,
        paymentPlan: isPaymentPlan,
        preferredPaymentMethodIndex,
        saveForFuture: isPaymentPlan,
        termsAcceptance,
        acknowledgedCreditCardFeesInCents: [
          fees[0] > 0 ? fees[0] : null,
          fees[1] > 0 ? fees[1] : null,
        ],
      }).unwrap();
    } catch (error: any) {
      // Capture failed — try to clean up the auths we created
      try {
        await cancelFinixSplitAuth({
          orderId: order.id,
          authorizationIds: authResponse.authorizationIds,
          failureReason: "capture_failed",
        }).unwrap();
      } catch (cancelError) {
        Sentry.captureMessage(
          "Failed to cancel Finix split auths after capture failure",
          {
            level: "warning",
            tags: {
              operation: "finix_split_cancel_after_capture_fail",
              orderId: order.id,
            },
            extra: { cancelError: JSON.stringify(cancelError) },
          }
        );
      }

      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Payment capture failed. Please try again.";

      Sentry.captureMessage("Finix split payment capture failed", {
        level: "error",
        tags: { operation: "finix_split_capture_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          firstAmountCents,
          secondAmountCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
      setIsProcessing(false);
      return;
    }

    // Success
    pageViewEvents(
      {
        accountName: order.account?.name,
        orderType: order.orderType,
        communicationMethod: order.patient?.preferredContactMethod,
      },
      events.PURCHASE
    );

    if (onUpdateOrder) {
      onUpdateOrder();
    }

    setShowPaymentSection({
      paymentPlan: false,
      paymentSection: false,
      paymentSuccess: true,
      receiptAvailable: true,
    });
    setIsProcessing(false);
  };

  const handleSplitPayment = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    const firstAmountCents = Math.round(firstCardAmount * 100);
    const secondAmountCents = Math.round(secondCardAmount * 100);

    let authResponse: ISplitAuthResult;
    // Which card is being tokenized, so a browser-side tokenization failure (nothing was
    // authorized) is reported as such rather than as an authorize failure.
    let tokenizingCard: 1 | 2 | null = 1;

    try {
      // Tokenize both cards sequentially
      const token1 = await tokenizeFirstCard();
      tokenizingCard = 2;
      const token2 = await tokenizeSecondCard();
      tokenizingCard = null;

      // Backend authorizes both cards
      authResponse = await createFinixSplitAuth({
        orderId: order.id,
        token1,
        amountInCents1: firstAmountCents,
        token2,
        amountInCents2: secondAmountCents,
        saveForFuture: isPaymentPlan,
        paymentPlan: isPaymentPlan,
        fraudSessionId: getFraudSessionId(),
      }).unwrap();
    } catch (error: any) {
      if (tokenizingCard) {
        const tokenizationError = error as IFinixTokenizationError;
        const errorMessage =
          tokenizationError?.message ||
          "Payment failed. Please check your card details and try again.";
        reportFinixTokenizationFailure({
          orderId: order.id,
          message: errorMessage,
          details: tokenizationError?.finixTokenizationError,
          flow: "split",
          cardIndex: tokenizingCard,
        });
        showError("Payment Failed", errorMessage);
        setIsProcessing(false);
        return;
      }
      const failedCard = error?.data?.failedCard;
      const reason = error?.data?.reason || error?.data?.message;
      const errorMessage = failedCard
        ? `Card ${failedCard} could not be authorized${
            reason ? `: ${reason}` : ""
          }. Please try a different card.`
        : error?.data?.message ||
          error?.message ||
          "Payment failed. Please check your card details and try again.";

      Sentry.captureMessage("Finix split payment authorize failed", {
        level: "error",
        tags: { operation: "finix_split_auth_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          firstAmountCents,
          secondAmountCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
      setIsProcessing(false);
      return;
    }

    // CLE-2798: the holds include each card's credit-card fee. If any applies, stop here and
    // show it — capture only after the patient confirms (or cancel the holds if they back out).
    const fees = authResponse.creditCardFeesInCents ?? [0, 0];
    const feesAlreadyShown = splitFeesMatchPreview(
      [firstCardPreviewRef.current, secondCardPreviewRef.current],
      fees,
      [firstAmountCents, secondAmountCents]
    );
    if ((fees[0] > 0 || fees[1] > 0) && !feesAlreadyShown) {
      setPendingSplitCapture({
        authResponse,
        firstAmountCents,
        secondAmountCents,
        termsAcceptance,
      });
      setIsProcessing(false);
      return;
    }

    setNoFeeCardConfirmed(
      orderCreditCardFeePercent > 0 && fees[0] + fees[1] === 0
    );
    await captureSplitPayment({
      authResponse,
      firstAmountCents,
      secondAmountCents,
      termsAcceptance,
    });
  };

  // === Pay-remaining with saved Finix PI ===
  // No tokenization — backend reuses the PI stored on the active PaymentPlan.
  const chargeSavedCard = async (
    termsAcceptance: ICreatePaymentTermsAcceptanceDto | undefined,
    creditCardFeeAmountInCents: number
  ) => {
    try {
      await payFinixRemainingWithSavedCard({
        orderId: order.id,
        amountInCents: totalAmountInCents,
        creditCardFeeAmountInCents,
        fraudSessionId: getFraudSessionId(),
        termsAcceptance,
      }).unwrap();

      pageViewEvents(
        {
          accountName: order.account?.name,
          orderType: order.orderType,
          communicationMethod: order.patient?.preferredContactMethod,
        },
        events.PURCHASE
      );

      if (onUpdateOrder) onUpdateOrder();

      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: false,
        paymentSuccess: true,
        receiptAvailable: true,
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "Payment failed. Please try again or use a different card.";

      Sentry.captureMessage("Finix pay-remaining (saved PI) failed", {
        level: "error",
        tags: {
          operation: "finix_pay_remaining_saved_pi_frontend",
          orderId: order.id,
        },
        extra: {
          orderId: order.id,
          amountInCents: totalAmountInCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSavedCardPayRemaining = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    let quote: IFinixCardFeeQuoteResponse;
    try {
      quote = await quoteFinixCardFee({
        orderId: order.id,
        useSavedPaymentMethod: true,
        amountInCents: totalAmountInCents,
      }).unwrap();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "We could not read your saved card. Please try a different card.";
      showError("Card Error", errorMessage);
      setIsProcessing(false);
      return;
    }
    if (quote.feeApplies) {
      // The patient already saw exactly this total before pressing Pay: charge it.
      if (
        quoteMatchesPreview(
          firstCardPreviewRef.current,
          quote,
          totalAmountInCents
        )
      ) {
        await chargeSavedCard(termsAcceptance, quote.feeAmountInCents);
        return;
      }
      setCardFeeQuote(quote);
      setIsProcessing(false);
      return;
    }
    setNoFeeCardConfirmed(orderCreditCardFeePercent > 0);
    await chargeSavedCard(termsAcceptance, 0);
  };

  // CLE-2798 — the patient confirmed the fee shown in the disclosure panel.
  const handleConfirmCreditCardFee = async () => {
    setPaymentError(null);
    setIsProcessing(true);
    if (pendingSplitCapture) {
      const pending = pendingSplitCapture;
      setPendingSplitCapture(null);
      await captureSplitPayment(pending);
      return;
    }
    if (!cardFeeQuote) {
      setIsProcessing(false);
      return;
    }
    const quote = cardFeeQuote;
    setCardFeeQuote(null);
    if (payRemainingPrice && useSavedPaymentMethod && savedPaymentMethod) {
      await chargeSavedCard(termsAcceptanceRef.current, quote.feeAmountInCents);
      return;
    }
    await chargeSingleCard({
      paymentInstrumentId: quote.paymentInstrumentId,
      creditCardFeeAmountInCents: quote.feeAmountInCents,
    });
  };

  // CLE-2798 — the patient backed out: release any holds and let them pick another card.
  const handleDeclineCreditCardFee = async () => {
    setCardFeeQuote(null);
    if (pendingSplitCapture) {
      const pending = pendingSplitCapture;
      setPendingSplitCapture(null);
      try {
        await cancelFinixSplitAuth({
          orderId: order.id,
          authorizationIds: pending.authResponse.authorizationIds,
          failureReason: "credit_card_fee_declined",
        }).unwrap();
      } catch (cancelError) {
        Sentry.captureMessage(
          "Failed to cancel Finix split auths after credit-card fee declined",
          {
            level: "warning",
            tags: {
              operation: "finix_split_cancel_fee_declined",
              orderId: order.id,
            },
            extra: { cancelError: JSON.stringify(cancelError) },
          }
        );
      }
    }
  };

  // CLE-2798: read each card in the background. New card: once every field is valid, and again after
  // an edit (the fields go incomplete, then complete). Saved card: right away. A two-card payment reads
  // both cards the same way, each for its own share.
  const totalAmountInCentsRef = useRef(totalAmountInCents);
  totalAmountInCentsRef.current = totalAmountInCents;
  const usingSavedCard = !!(
    payRemainingPrice &&
    useSavedPaymentMethod &&
    savedPaymentMethod
  );
  const readNewCard = async (
    form: IFinixPaymentFormHandle | null
  ): Promise<ICardFeePreview | null> => {
    if (!form) return null;
    const token = await form.tokenize();
    const quote = await quoteFinixCardFee({
      orderId: order.id,
      token,
      amountInCents: totalAmountInCentsRef.current,
    }).unwrap();
    return { feeApplies: quote.feeApplies, feePercent: quote.feePercent };
  };
  const firstCardRead = useCardFeePreview({
    enabled:
      orderCreditCardFeePercent > 0 && (usingSavedCard || firstCardComplete),
    sourceKey: `${order.id}:${usingSavedCard ? "saved" : "new"}`,
    read: async () => {
      if (!usingSavedCard) return readNewCard(firstFormRef.current);
      const quote = await quoteFinixCardFee({
        orderId: order.id,
        useSavedPaymentMethod: true,
        amountInCents: totalAmountInCentsRef.current,
      }).unwrap();
      return { feeApplies: quote.feeApplies, feePercent: quote.feePercent };
    },
  });
  const secondCardRead = useCardFeePreview({
    enabled:
      orderCreditCardFeePercent > 0 && isSplitPayment && secondCardComplete,
    sourceKey: order.id,
    read: () => readNewCard(secondFormRef.current),
  });
  const firstCardPreview = firstCardRead.preview;
  const secondCardPreview = secondCardRead.preview;
  firstCardPreviewRef.current = firstCardPreview;
  secondCardPreviewRef.current = isSplitPayment ? secondCardPreview : null;
  const cardPreviewLoading = firstCardRead.loading || secondCardRead.loading;

  // CLE-2798: the design's amount / surcharge / total rows under the amount, or "No payment fee".
  const feeSummaryState = useMemo<PaymentFeeSummaryState | null>(() => {
    if (pendingSplitCapture) {
      const fees = pendingSplitCapture.authResponse.creditCardFeesInCents ?? [
        0, 0,
      ];
      const cardLine = (label: string, fee: number) =>
        fee > 0
          ? `${label}: ${parsePrice(
              centsToDollars(fee)
            )} surcharge (credit card)`
          : `${label}: no fee`;
      return {
        kind: "surcharge",
        amountInCents:
          pendingSplitCapture.firstAmountCents +
          pendingSplitCapture.secondAmountCents,
        surchargeInCents: fees[0] + fees[1],
        percent:
          pendingSplitCapture.authResponse.creditCardFeePercent ??
          orderCreditCardFeePercent,
        perCardLines: [
          cardLine("First card", fees[0]),
          cardLine("Second card", fees[1]),
        ],
      };
    }
    if (cardFeeQuote) {
      return {
        kind: "surcharge",
        amountInCents:
          cardFeeQuote.totalAmountInCents - cardFeeQuote.feeAmountInCents,
        surchargeInCents: cardFeeQuote.feeAmountInCents,
        percent: cardFeeQuote.feePercent,
      };
    }
    if (isSplitPayment && (firstCardPreview || secondCardPreview)) {
      const amounts = [
        Math.round(firstCardAmount * 100),
        Math.round(secondCardAmount * 100),
      ];
      const previews = [firstCardPreview, secondCardPreview];
      const fees = previews.map((preview, i) =>
        preview?.feeApplies
          ? creditCardFeeForAmount(preview.feePercent, amounts[i])
          : 0
      );
      const cardLine = (label: string, i: number) => {
        if (!previews[i]) return `${label}: enter the card to see its fee`;
        return fees[i] > 0
          ? `${label}: ${parsePrice(
              centsToDollars(fees[i])
            )} surcharge (credit card)`
          : `${label}: no fee`;
      };
      if (fees[0] + fees[1] > 0) {
        return {
          kind: "surcharge",
          amountInCents: amounts[0] + amounts[1],
          surchargeInCents: fees[0] + fees[1],
          percent:
            previews.find((preview) => preview?.feeApplies)?.feePercent ??
            orderCreditCardFeePercent,
          perCardLines: [cardLine("First card", 0), cardLine("Second card", 1)],
        };
      }
      // "No payment fee" only once BOTH cards are known to carry none.
      return firstCardPreview && secondCardPreview ? { kind: "noFee" } : null;
    }
    if (!isSplitPayment && firstCardPreview) {
      if (!firstCardPreview.feeApplies) return { kind: "noFee" };
      return {
        kind: "surcharge",
        amountInCents: totalAmountInCents,
        surchargeInCents: creditCardFeeForAmount(
          firstCardPreview.feePercent,
          totalAmountInCents
        ),
        percent: firstCardPreview.feePercent,
      };
    }
    if (noFeeCardConfirmed && isProcessing) return { kind: "noFee" };
    return null;
  }, [
    pendingSplitCapture,
    cardFeeQuote,
    firstCardPreview,
    secondCardPreview,
    isSplitPayment,
    firstCardAmount,
    secondCardAmount,
    totalAmountInCents,
    noFeeCardConfirmed,
    isProcessing,
    orderCreditCardFeePercent,
  ]);
  feeSummaryStateRef.current = feeSummaryState;
  // A confirmation is pending only when Pay found a surcharge the patient had not seen yet.
  const surchargeDue =
    (cardFeeQuote || pendingSplitCapture) &&
    feeSummaryState?.kind === "surcharge"
      ? feeSummaryState
      : null;
  // Ani: the red notice under the card fields whenever a credit card's surcharge is on screen.
  const surchargeAlertPercent =
    feeSummaryState?.kind === "surcharge" &&
    feeSummaryState.surchargeInCents > 0
      ? feeSummaryState.percent
      : 0;
  const payNowTotalInCents =
    feeSummaryState?.kind === "surcharge"
      ? feeSummaryState.amountInCents + feeSummaryState.surchargeInCents
      : totalAmountInCents;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setPaymentError(null);
    setNoFeeCardConfirmed(false);
    // A background card read still in flight must not land in the middle of the payment.
    firstCardRead.cancel();
    secondCardRead.cancel();

    let context = TermsAcceptanceContext.FullPayment;
    if (payRemainingPrice) {
      context = TermsAcceptanceContext.PayRemaining;
    } else if (isPaymentPlan) {
      context = TermsAcceptanceContext.DownPayment;
    }
    const termsAcceptance = await buildTermsAcceptance(context);
    // Cache for the single-card flow; its onTokenSuccess callback fires later
    // and can't easily receive parameters.
    termsAcceptanceRef.current = termsAcceptance;

    debouncedPageViewEvents(
      {
        accountName: order.account?.name,
        orderType: order.orderType,
        communicationMethod: order.patient?.preferredContactMethod,
      },
      events.CLICK_PURCHASE
    );

    // Pay-remaining with saved PI — no tokenization needed
    if (payRemainingPrice && useSavedPaymentMethod && savedPaymentMethod) {
      handleSavedCardPayRemaining(termsAcceptance).catch((err) => {
        Sentry.captureException(err, {
          tags: {
            operation: "finix_pay_remaining_saved_pi_unhandled",
            orderId: order.id,
          },
        });
        showError(
          "Payment Failed",
          "An unexpected error occurred. Please refresh and try again."
        );
        setIsProcessing(false);
      });
      return;
    }

    if (isSplitPayment) {
      handleSplitPayment(termsAcceptance).catch((err) => {
        Sentry.captureException(err, {
          tags: { operation: "finix_split_unhandled", orderId: order.id },
        });
        showError(
          "Payment Failed",
          "An unexpected error occurred. Please refresh and try again."
        );
        setIsProcessing(false);
      });
      return;
    }

    // Single-card path — also handles pay-remaining with a new card
    if (firstFormRef.current) {
      firstFormRef.current.submit();
    } else {
      setIsProcessing(false);
      showError("Error", "Card form is not ready. Please try again.");
    }
  };

  const handleOpenPrivacyPolicy = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenLegalDocument("privacy");
  };

  const handleOpenTermsAndConditions = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenLegalDocument("terms");
  };

  if (showPaymentSection.paymentSuccess) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={order.externalId || ""}
        receiptAvailable={showPaymentSection.receiptAvailable}
      />
    );
  }

  // Stripe parity: a partial pay-remaining (amount < clearPrice) shows the preferred-card radio
  // so the patient can choose whether to switch the saved card to the new card after the charge.
  const isPartialPayRemaining =
    payRemainingPrice && editableRemainingAmount !== prices.clearPrice;

  return (
    <ContainerForm>
      {isPaymentPlan && !isDownPaymentOnly && <HeaderPanel />}
      <div className="flex flex-column">
        <span className="title">
          <span>{headerTitle}</span>
          <span>{parsePrice(displayAmount)}</span>
        </span>
      </div>
      {isPaymentPlan && !isDownPaymentOnly && (
        <div className="flex flex-column my-3 align-items-center">
          <p className="text-sm font-normal" style={{ color: "#64636E" }}>
            {parsePrice(installmentPaymentForPaymentPlan)}
            {installmentFrequencySuffix}
            {"   "} <span className="ml-2">{paymentPlanDuration} payments</span>
          </p>
          <p className="text-sm font-normal" style={{ color: "#64636E" }}>
            Next payment due {nextInstallmentDueDate}
          </p>
        </div>
      )}

      {payRemainingPrice && remainingInstallmentsInfo && (
        <div className="flex flex-column my-3 align-items-center">
          <p className="text-sm font-normal" style={{ color: "#64636E" }}>
            {remainingInstallmentsInfo.remainingPayments} payments left
            <span className="mx-2">→</span>
            {parsePrice(
              centsToDollars(remainingInstallmentsInfo.installmentAmountInCents)
            )}
            {remainingInstallmentsInfo.frequencySuffix}
          </p>
          {remainingInstallmentsInfo.nextDateDue && (
            <p className="text-sm font-normal" style={{ color: "#64636E" }}>
              Next payment scheduled for{" "}
              {dayjs
                .utc(remainingInstallmentsInfo.nextDateDue)
                .format("M/D/YYYY")}
            </p>
          )}
        </div>
      )}
      {payRemainingPrice &&
        remainingInstallmentsInfo?.nextDateDue &&
        editableRemainingAmount < prices.clearPrice &&
        !autopayWarningDismissed && (
          <AutopayWarningBanner role="alert">
            <i
              className="pi pi-info-circle mt-1"
              style={{ color: "#DC6803" }}
            />
            <span className="text-sm text-left flex-1">
              This payment does not replace your upcoming automatic payment on{" "}
              {dayjs
                .utc(remainingInstallmentsInfo.nextDateDue)
                .format("M/D/YYYY")}
              .
            </span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setAutopayWarningDismissed(true)}
              className="close-button"
            >
              <i className="pi pi-times" style={{ fontSize: "0.875rem" }} />
            </button>
          </AutopayWarningBanner>
        )}
      {/* Pay-remaining: custom amount input */}
      {payRemainingPrice && (
        <div className="flex flex-column mt-4 mb-4">
          <label htmlFor="custom-amount" className="text-sm mb-1">
            Custom Amount:
          </label>
          <StyledInputNumber
            id="custom-amount"
            value={editableRemainingAmount}
            mode="currency"
            currency="USD"
            locale="en-US"
            min={1}
            max={prices.clearPrice}
            onValueChange={handleAmountChange}
          />
        </div>
      )}

      {overviewContent?.discountExpires &&
        !payRemainingPrice &&
        !isPaymentPlan &&
        !notScheduledYet && (
          <span className="mt-1 font-semibold text-base line-height-3 text-center warning">
            Discount expires on{" "}
            {dayjs
              .utc(order.dateOfService)
              .add(expirationDays, "day")
              .format("MMMM D, YYYY")}
          </span>
        )}

      {/* CLE-2798: surcharge disclosure BEFORE the card is entered (card-brand rule). */}
      {orderCreditCardFeePercent > 0 && (
        // A single card's form brings its own top margin; the bordered card panels (two cards,
        // pay-remaining) do not, so the box keeps the same gap above them itself.
        <div
          className={`mt-3${
            isSplitPayment || payRemainingPrice ? " mb-4" : ""
          }`}
        >
          <PaymentMethodFeesNotice
            percent={orderCreditCardFeePercent}
            isPaymentPlan={!!isPaymentPlan}
          />
        </div>
      )}

      {/* Pay-remaining: saved card (from PaymentPlan.finixPaymentInstrumentId) */}
      {payRemainingPrice && savedPaymentMethod && (
        <>
          <SavedCard
            savedPaymentMethod={savedPaymentMethod}
            showFooter={!useSavedPaymentMethod && isPartialPayRemaining}
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            radioValue={0}
          />
          {useSavedPaymentMethod && (
            <Button
              className="p-button-text p-0 mb-4"
              onClick={(e) => {
                e.preventDefault();
                setUseSavedPaymentMethod(!useSavedPaymentMethod);
              }}
            >
              <i className="pi pi-plus-circle mr-2" />
              Use a different card
            </Button>
          )}
        </>
      )}

      {/* Pay-remaining with a new (not saved) card — render the Finix form inside a CardAccordion
          that also carries the "use this card for future payments" radio on partial payments. */}
      {payRemainingPrice &&
        !useSavedPaymentMethod &&
        !isSavedPaymentMethodLoading && (
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="New Card Information"
              isFirstCard={false}
              showFooter={!!savedPaymentMethod && isPartialPayRemaining}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={0}
              handleRemoveSecondCard={
                savedPaymentMethod
                  ? () => {
                      setUseSavedPaymentMethod(!useSavedPaymentMethod);
                      setPreferredPaymentMethodIndex(0);
                    }
                  : undefined
              }
              footerText="Use this card for future payments"
            >
              <FinixPaymentForm
                ref={firstFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setFirstCardComplete}
                onTokenSuccess={handleFirstCardTokenSuccess}
                onTokenError={handleFirstCardTokenError}
              />
            </CardAccordion>
          </div>
        )}

      {/* Non-pay-remaining: first card — always rendered inside a single CardAccordion so the
          FinixPaymentForm keeps a stable position in the React tree. Toggling isSplitPayment only
          flips showStylings/showFooter; the form is NOT remounted, so card input the user already
          entered is preserved when they click "Split payment between two cards". Mirrors the Stripe
          PaymentSection pattern. (Pay-remaining has its own card-input branch above.) */}
      {!payRemainingPrice && (
        <div
          className={`sensitive${isSplitPayment ? "" : " mt-4"}`}
          data-tour={TourAnchor.CardForm}
        >
          <CardAccordion
            showStylings={isSplitPayment}
            title="First Card"
            isFirstCard
            showFooter={isPaymentPlan && isSplitPayment}
            radioValue={0}
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            cardAmount={firstCardAmount}
          >
            <FinixPaymentForm
              ref={firstFormRef}
              applicationId={finixApplicationId}
              environment={finixEnvironment}
              onCardComplete={setFirstCardComplete}
              onTokenSuccess={handleFirstCardTokenSuccess}
              onTokenError={handleFirstCardTokenError}
            />
          </CardAccordion>
        </div>
      )}

      {/* CLE-2798, final v0 layout: the notice, then amount / surcharge / total in a box, right under the
          card fields. A two-card payment sums both cards, so its block sits under the second card. */}
      {!isSplitPayment && (
        <>
          <CreditCardSurchargeAlert percent={surchargeAlertPercent} />
          <PaymentFeeSummary state={feeSummaryState} />
        </>
      )}

      {/* Add/Remove second card — hidden in pay-remaining mode (split payment is not supported
          for pay-remaining, matching Stripe behavior in PaymentSection.tsx:1287) */}
      {!payRemainingPrice && !isSplitPayment && (
        <StyledSplitButton
          onClick={handleAddSecondCard}
          disabled={isProcessing}
          data-tour={TourAnchor.SplitPayment}
        >
          <i
            className="pi pi-plus-circle"
            style={{ marginRight: "4px", marginTop: "2px" }}
          />
          <div className="flex flex-column ml-2 text-left gap-1">
            <span className="font-semibold text-sm">
              Split payment between two cards
            </span>
            <span className="font-normal text-xs">
              Divide your {parsePrice(displayAmount)} payment across two
              different payment methods
            </span>
          </div>
        </StyledSplitButton>
      )}
      {!payRemainingPrice && isSplitPayment && (
        <>
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="Second Card"
              isFirstCard={false}
              showFooter={isPaymentPlan && isSplitPayment}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={secondCardAmount}
              handleRemoveSecondCard={handleRemoveSecondCard}
              isProcessing={isProcessing}
            >
              <FinixPaymentForm
                ref={secondFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setSecondCardComplete}
                onTokenSuccess={handleSecondCardTokenSuccess}
                onTokenError={handleSecondCardTokenError}
              />
            </CardAccordion>
          </div>

          <PaymentSplitSection
            firstCardAmount={firstCardAmount}
            secondCardAmount={secondCardAmount}
            totalPaymentAmount={totalPaymentAmount}
            onFirstCardAmountChange={handlePaymentSplitFirstCardChange}
            onSecondCardAmountChange={handlePaymentSplitSecondCardChange}
          />
          <CreditCardSurchargeAlert percent={surchargeAlertPercent} />
          <PaymentFeeSummary state={feeSummaryState} />
        </>
      )}

      <div
        className="flex align-items-start gap-2 mt-2 text-left"
        data-tour={TourAnchor.TermsCheckbox}
      >
        <Checkbox
          inputId="finix-payment-terms-acceptance"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.checked ?? false)}
          className="mt-1 flex-shrink-0"
        />
        <label
          htmlFor="finix-payment-terms-acceptance"
          className="text-sm gray font-normal cursor-pointer m-0 line-height-3"
        >
          {`By clicking "Pay Now", I confirm that I have read and agree to the`}{" "}
          <Button
            type="button"
            className="p-button-text text-sm p-0 border-none"
            onClick={handleOpenPrivacyPolicy}
          >
            Privacy Policy
          </Button>
          {legalDocuments?.data?.privacyPolicy && (
            <>
              {" "}
              (version: {legalDocuments.data.privacyPolicy.version}, effective
              from {parseDate(legalDocuments.data.privacyPolicy.effectiveAt)})
            </>
          )}{" "}
          and{" "}
          <Button
            type="button"
            className="p-button-text text-sm p-0 border-none"
            onClick={handleOpenTermsAndConditions}
          >
            Terms and Conditions
          </Button>
          {legalDocuments?.data?.termsAndConditions && (
            <>
              {" "}
              (version: {legalDocuments.data.termsAndConditions.version},
              effective from{" "}
              {parseDate(
                legalDocuments.data.termsAndConditions.effectiveAt,
                false,
                true
              )}
              )
            </>
          )}
        </label>
      </div>

      {isPaymentPlan && (
        <p className="text-sm gray mt-2 text-left font-normal">
          I authorize Clear Health to automatically charge the provided credit
          card for the specified (and updated if applicable) amount in the
          Payment Plan for each billing period
        </p>
      )}

      <p className="text-sm gray mt-2 text-center font-normal mt-4">
        <i className="pi pi-lock mr-2 text-sm " />
        All transactions are secure and encrypted
      </p>

      {paymentError && (
        <div
          role="alert"
          data-testid="finix-payment-error-banner"
          className="p-3 mt-2 border-1 border-round"
          style={{
            backgroundColor: "#fdecea",
            borderColor: "#f44336",
            color: "#611a15",
          }}
        >
          <i className="pi pi-exclamation-triangle mr-2" />
          <span className="text-sm">{paymentError}</span>
        </div>
      )}

      {/* CLE-2798: a credit card was read. The surcharge is in the rows under the amount; charge
          only on an explicit confirmation, or back out with nothing charged. */}
      {surchargeDue && (
        <div
          role="status"
          data-testid="credit-card-fee-confirm"
          className="mt-3 text-left"
        >
          <StyledButton
            type="button"
            loading={isProcessing}
            data-testid="button-confirm-credit-card-fee"
            className="w-full justify-content-center"
            onClick={handleConfirmCreditCardFee}
          >
            <span className="font-bold">
              Pay Now ·{" "}
              {parsePrice(
                centsToDollars(
                  surchargeDue.amountInCents + surchargeDue.surchargeInCents
                )
              )}
            </span>
          </StyledButton>
          <Button
            type="button"
            className="p-button-text w-full justify-content-center mt-1"
            data-testid="button-decline-credit-card-fee"
            disabled={isProcessing}
            onClick={handleDeclineCreditCardFee}
          >
            Use a different card
          </Button>
        </div>
      )}

      {!surchargeDue && (
        <StyledButton
          type="submit"
          loading={isProcessing}
          data-testid="button-submit-pay-now"
          data-tour={TourAnchor.PayNow}
          className="w-full justify-content-center mt-2"
          onClick={handleSubmit}
          disabled={payNowDisabled || cardPreviewLoading}
        >
          <span className="font-bold">
            {cardPreviewLoading
              ? "Checking your card…"
              : `Pay Now · ${parsePrice(centsToDollars(payNowTotalInCents))}`}
          </span>
        </StyledButton>
      )}

      {onReturnClick && <BackToDetailsButton onClick={onReturnClick} />}

      <LegalDocumentDialog
        visible={openLegalDocument === "terms"}
        onClose={() => setOpenLegalDocument(null)}
        header="Terms of Use"
        content={legalDocuments?.data?.termsAndConditions?.content}
        isLoading={isLegalDocumentsFetching}
      />
      <LegalDocumentDialog
        visible={openLegalDocument === "privacy"}
        onClose={() => setOpenLegalDocument(null)}
        header="Privacy Policy"
        content={legalDocuments?.data?.privacyPolicy?.content}
        isLoading={isLegalDocumentsFetching}
      />
    </ContainerForm>
  );
}
